import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  rootId,
  TreeItem,
  TreeItemTypes,
} from 'shared/components/ui/virtualized-tree-list';
import { ID } from 'shared/types/id';
import { TypeSections } from 'shared/types/page-sections';
import { SortingMode } from 'shared/types/sorting-mode';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';

import { filterEmptyChildrenCascading } from './use-life-area-tree';

export type Options = {
  tasks: Task[];
  customOrder?: Record<ID, ID[]>;
  sortingMode?: SortingMode | null;
  today: Timestamp;
  weekStartsOn: WeekDays;
  showEmptyGroups?: boolean;
};

export const useTypeTree = ({
  tasks,
  customOrder,
  sortingMode,
  today,
  weekStartsOn,
  showEmptyGroups,
}: Options) => {
  const { t } = useTranslation();

  return useMemo(() => {
    const allTreeItems: Record<ID, TreeItem> = {
      [TypeSections.Tasks]: {
        id: TypeSections.Tasks,
        type: TreeItemTypes.Type,
        name: t('general.sections.type.tasks.title'),
        children: [],
        isFolder: true,
      },
      [TypeSections.Habits]: {
        id: TypeSections.Habits,
        type: TreeItemTypes.Type,
        name: t('general.sections.type.habits.title'),
        children: [],
        isFolder: true,
      },
    };

    // Sort tasks for Tasks section (including repeating tasks)
    const todoTasks = tasks.filter((task) => task.type !== TaskType.Habit);
    const sortedTodoTasks = getSortedTasks(todoTasks, {
      today,
      weekStartsOn,
      order:
        sortingMode === SortingMode.Custom
          ? customOrder?.[TypeSections.Tasks] ?? []
          : [],
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    });

    // Sort tasks for Habits section
    const habitTasks = tasks.filter((task) => task.type === TaskType.Habit);
    const sortedHabitTasks = getSortedTasks(habitTasks, {
      today,
      weekStartsOn,
      order:
        sortingMode === SortingMode.Custom
          ? customOrder?.[TypeSections.Habits] ?? []
          : [],
      mode: sortingMode ? [SortingMode.DueDate, sortingMode] : undefined,
    });

    // Populate tree items
    // Tasks
    sortedTodoTasks.forEach((task) => {
      if (allTreeItems[TypeSections.Tasks]) {
        allTreeItems[TypeSections.Tasks].children?.push(task.id);
      }
      allTreeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
    });

    // Add New Task row to Tasks
    if (
      allTreeItems[TypeSections.Tasks] &&
      (showEmptyGroups || allTreeItems[TypeSections.Tasks].children?.length)
    ) {
      const newTaskItemId = `${TypeSections.Tasks}-new-task`;
      allTreeItems[TypeSections.Tasks].children?.push(newTaskItemId);
      allTreeItems[newTaskItemId] = {
        id: newTaskItemId,
        name: 'New Task',
        type: TreeItemTypes.NewTask,
      };
    }

    // Habits
    sortedHabitTasks.forEach((task) => {
      if (allTreeItems[TypeSections.Habits]) {
        allTreeItems[TypeSections.Habits].children?.push(task.id);
      }
      allTreeItems[task.id] = {
        id: task.id,
        name: task.name,
        type: TreeItemTypes.Task,
      };
    });

    // Add New Task row to Habits
    if (
      allTreeItems[TypeSections.Habits] &&
      (showEmptyGroups || allTreeItems[TypeSections.Habits].children?.length)
    ) {
      const newHabitItemId = `${TypeSections.Habits}-new-task`;
      allTreeItems[TypeSections.Habits].children?.push(newHabitItemId);
      allTreeItems[newHabitItemId] = {
        id: newHabitItemId,
        name: 'New Habit',
        type: TreeItemTypes.NewTask,
      };
    }

    // Filter empty sections
    const filteredTreeItems = filterEmptyChildrenCascading(allTreeItems);
    const topLevelIds = Object.keys(filteredTreeItems).filter((id) =>
      [TypeSections.Tasks, TypeSections.Habits].includes(id as TypeSections),
    );

    return {
      [rootId]: {
        id: rootId,
        name: 'Root',
        type: TreeItemTypes.Root,
        children: topLevelIds,
      },
      ...filteredTreeItems,
    };
  }, [
    customOrder,
    sortingMode,
    t,
    tasks,
    today,
    weekStartsOn,
    showEmptyGroups,
  ]);
};
